import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageShell, goldButton, redButton } from '../components/PageShell';
import { useAuth } from '../context/AuthContext';
import { orderApi } from '../lib/orderApi';
import { AdminOrders, AdminProducts, AdminRevenue, AdminSettings, AdminWebhooks, AdminAuditLogs, AdminTrending, AdminDrivers } from '../components/admin';
import { RoutePreviewModal } from '../components/admin/RoutePreviewModal';
import { TicketModal } from '../components/admin/TicketModal';
import { OfferPanel } from '../components/admin/OfferPanel';

const AdminDashboard = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  // Core state
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState({});
  const [route, setRoute] = useState(null);
  const [routePreview, setRoutePreview] = useState(null);
  const [routeBusy, setRouteBusy] = useState(false);
  const [routeError, setRouteError] = useState('');
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState('');

  // Driver sharing
  const [sharing, setSharing] = useState(false);
  const watchRef = useRef(null);

  // Settings
  const [cfgOpen, setCfgOpen] = useState(false);
  const [cfg, setCfg] = useState(null);

  // Panels
  const [offerOpen, setOfferOpen] = useState(false);
  const [mktCount, setMktCount] = useState(null);
  const [revOpen, setRevOpen] = useState(false);
  const [prodOpen, setProdOpen] = useState(false);
  const [webhookOpen, setWebhookOpen] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const [trendingOpen, setTrendingOpen] = useState(false);
  const [driversOpen, setDriversOpen] = useState(false);

  // Ticket modal
  const [ticket, setTicket] = useState(null);

  // Reminders
  const [reminderMsg, setReminderMsg] = useState('');
  const [reminderBusy, setReminderBusy] = useState(false);

  const load = useCallback(async () => {
    setLoadError('');
    try {
      const { data } = await orderApi.get('/admin/orders');
      setOrders(data.orders);
      setReady(true);
    } catch {
      setLoadError('Error al cargar órdenes');
      setReady(true);
    }
    try { const r = await orderApi.get('/admin/marketing-count'); setMktCount(r.data.count); } catch { setMktCount(null); }
    try { const c = await orderApi.get('/admin/settings'); setCfg(c.data.settings); } catch { setCfg(null); }
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate('/login?next=/admin'); return; }
    if (user.role !== 'admin') { navigate('/'); return; }
    load();
  }, [user, loading, navigate, load]);

  useEffect(() => () => { if (watchRef.current != null) navigator.geolocation.clearWatch(watchRef.current); }, []);

  const changeStatus = async (orderId, status) => { await orderApi.patch(`/admin/orders/${orderId}/status`, { status }); load(); };
  const toggleSelect = (orderId) => setSelected((p) => ({ ...p, [orderId]: !p[orderId] }));
  const confirmReceipt = async (orderId) => {
    try {
      await orderApi.post(`/admin/orders/${orderId}/confirm-receipt`);
      load();
    } catch (e) {
      alert('Error al confirmar recibo: ' + (e.response?.data?.detail || e.message));
    }
  };

  const sendReminders = async () => {
    setReminderBusy(true);
    setReminderMsg('');
    try {
      const { data } = await orderApi.post('/admin/orders/send-reminders');
      setReminderMsg(`✓ Enviado ${data.sent} recordatorio(s) de ${data.eligible} elegible(s).`);
      load();
    } catch (e) {
      setReminderMsg('Error: ' + (e.response?.data?.detail || e.message));
    } finally {
      setReminderBusy(false);
    }
  };

  const previewRoute = async (ids) => {
    if (!ids.length) return;
    setRouteError('');
    setRouteBusy(true);
    try {
      const { data } = await orderApi.post('/admin/route/preview', { order_ids: ids });
      setRoutePreview({ ids, data });
    } catch (e) {
      setRouteError(e.response?.data?.detail || 'No se pudo calcular la vista previa de la ruta.');
    } finally {
      setRouteBusy(false);
    }
  };

  const confirmSendRoute = async () => {
    if (!routePreview) return;
    setRouteBusy(true);
    try {
      const { data } = await orderApi.post('/admin/route', { order_ids: routePreview.ids });
      setRoute(data);
      window.open(data.maps_url, '_blank');
      setRoutePreview(null);
      load();
    } catch (e) {
      setRouteError(e.response?.data?.detail || 'No se pudo enviar la ruta al chofer.');
    } finally {
      setRouteBusy(false);
    }
  };
  const selectedIds = Object.keys(selected).filter((k) => selected[k]);

  const toggleShare = () => {
    if (sharing) {
      if (watchRef.current != null) navigator.geolocation.clearWatch(watchRef.current);
      watchRef.current = null;
      orderApi.post('/admin/driver-location/stop').catch(() => {});
      setSharing(false);
    } else {
      if (!navigator.geolocation) { alert('Tu dispositivo no soporta geolocalización.'); return; }
      watchRef.current = navigator.geolocation.watchPosition(
        (pos) => orderApi.post('/admin/driver-location', { lat: pos.coords.latitude, lng: pos.coords.longitude }).catch(() => {}),
        () => {},
        { enableHighAccuracy: true, maximumAge: 5000, timeout: 20000 }
      );
      setSharing(true);
    }
  };

  if (loading || !ready) {
    return (
      <PageShell>
        <p className="font-body" style={{ fontSize: '14px', color: '#C2C1B6' }}>Cargando...</p>
      </PageShell>
    );
  }

  return (
    <PageShell>
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display" style={{ fontSize: '34px', fontWeight: 700 }}>Panel de Órdenes</h1>
        <div className="flex gap-3 flex-wrap">
          <button onClick={load} style={goldButton} data-testid="admin-refresh">Actualizar</button>
          <button onClick={toggleShare} style={sharing ? { ...redButton } : { ...goldButton }} data-testid="driver-share-toggle">
            {sharing ? '● Compartiendo ubicación (detener)' : 'Compartir mi ubicación (chofer)'}
          </button>
          <button onClick={() => setCfgOpen((v) => !v)} style={goldButton} data-testid="settings-toggle">
            Configuración
          </button>
          <button onClick={() => setRevOpen((v) => !v)} style={goldButton} data-testid="revenue-toggle">
            Ventas
          </button>
          <button onClick={() => setOfferOpen((v) => !v)} style={goldButton} data-testid="offer-toggle">
            Enviar oferta
          </button>
          <button onClick={sendReminders} disabled={reminderBusy} style={{ ...goldButton, opacity: reminderBusy ? 0.6 : 1 }} data-testid="send-reminders">
            {reminderBusy ? 'Enviando…' : 'Enviar recordatorios'}
          </button>
          <button onClick={() => setProdOpen((v) => !v)} style={goldButton} data-testid="products-toggle">
            Productos
          </button>
          <button onClick={() => setTrendingOpen((v) => !v)} style={goldButton} data-testid="trending-toggle">
            Trending
          </button>
          <button onClick={() => setDriversOpen((v) => !v)} style={goldButton} data-testid="drivers-toggle">
            Choferes
          </button>
          <button onClick={() => setWebhookOpen((v) => !v)} style={goldButton} data-testid="webhook-toggle">
            Webhooks
          </button>
          <button onClick={() => setAuditOpen((v) => !v)} style={goldButton} data-testid="audit-toggle">
            Audit Logs
          </button>
        </div>
      </div>

      {/* Selected actions */}
      {selectedIds.length > 0 && (
        <div className="mt-4 flex items-center gap-3">
          <span className="font-body" style={{ fontSize: '12px', color: '#C2C1B6' }}>{selectedIds.length} seleccionado(s)</span>
          <button onClick={() => previewRoute(selectedIds)} style={goldButton} disabled={routeBusy} data-testid="route-selected">
            {routeBusy ? 'Calculando…' : 'Vista previa: ruta de seleccionados'}
          </button>
        </div>
      )}

      {/* Error message */}
      {loadError && (
        <div className="mt-4 p-3" style={{ background: 'rgba(200,40,43,0.15)', border: '1px solid rgba(200,40,43,0.4)' }}>
          <p className="font-body" style={{ fontSize: '13px', color: '#E0736F' }}>{loadError}</p>
        </div>
      )}

      {/* Route error message */}
      {routeError && (
        <div className="mt-4 p-3" style={{ background: 'rgba(200,40,43,0.15)', border: '1px solid rgba(200,40,43,0.4)' }}>
          <p className="font-body" style={{ fontSize: '13px', color: '#E0736F' }} data-testid="route-error">{routeError}</p>
        </div>
      )}

      {/* Reminders message */}
      {reminderMsg && (
        <div className="mt-4 p-3" style={{ background: reminderMsg.startsWith('✓') ? 'rgba(127,183,126,0.15)' : 'rgba(200,40,43,0.15)', border: reminderMsg.startsWith('✓') ? '1px solid rgba(127,183,126,0.4)' : '1px solid rgba(200,40,43,0.4)' }}>
          <p className="font-body" style={{ fontSize: '13px', color: reminderMsg.startsWith('✓') ? '#7FB77E' : '#E0736F' }} data-testid="reminder-result">{reminderMsg}</p>
        </div>
      )}

      <AdminSettings isOpen={cfgOpen} cfg={cfg} setCfg={setCfg} onRefresh={load} />
      <AdminProducts isOpen={prodOpen} />
      <AdminTrending isOpen={trendingOpen} />
      <AdminDrivers isOpen={driversOpen} />
      <AdminRevenue isOpen={revOpen} />
      <OfferPanel isOpen={offerOpen} mktCount={mktCount} />
      <AdminWebhooks isOpen={webhookOpen} />
      <AdminAuditLogs isOpen={auditOpen} />

      {/* Orders List */}
      {ready && (
        <AdminOrders
          orders={orders}
          selected={selected}
          toggleSelect={toggleSelect}
          changeStatus={changeStatus}
          confirmReceipt={confirmReceipt}
          previewRoute={previewRoute}
          setTicket={setTicket}
          route={route}
        />
      )}

      {routePreview && (
        <RoutePreviewModal
          preview={routePreview}
          busy={routeBusy}
          onConfirm={confirmSendRoute}
          onCancel={() => setRoutePreview(null)}
        />
      )}

      {ticket && <TicketModal ticket={ticket} onClose={() => setTicket(null)} />}
    </PageShell>
  );
};

export default AdminDashboard;
